/**
 * Button Theme Configuration
 *
 * Defines all button variants, sizes, and states using MUI's theme system
 * This centralizes all button styling and improves performance by computing styles once
 */

import { Components, Theme } from "@mui/material/styles";
import { SemanticTokens } from "../tokens/semantic";

export type AmeenButtonVariant =
  | "primary"
  | "secondary"
  | "orderTypeActive"
  | "outlined"
  | "text"
  | "light"
  | "dimed"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "cancel"
  | "back"
  | "backPos"
  | "backBackOffice"
  | "toolbarAction"
  | "compactAction"
  | "attentionOutlined"
  | "cancelNeutral"
  | "outlinedSecondary"
  | "confirmAction"
  | "outlinedMuted"
  | "detailsAction"
  | "secondaryTinted"
  | "filterClear"
  | "translateAction"
  | "outlinedTertiary"
  | "dangerFlat"
  | "primaryFlat"
  | "outlinedPrimary";

export type AmeenButtonSize = "small" | "medium" | "large";

/**
 * Creates the MuiButton theme configuration with all custom variants
 */
export const createButtonTheme = (
  colors: SemanticTokens,
): Components<Theme>["MuiButton"] => ({
  defaultProps: {
    disableElevation: true,
    disableRipple: false,
  },

  styleOverrides: {
    root: {
      fontWeight: 500,
      textTransform: "none",
      boxShadow: "none",
      borderRadius: 8,

      "&:hover": {
        boxShadow: "none",
      },

      "&:active": {
        boxShadow: "none",
      },

      "& .MuiButton-startIcon, & .MuiButton-endIcon": {
        margin: 0,
      },
    },
  },

  variants: [
    // ═══════════════════════════════════════════════════════════
    // VARIANT STYLES
    // ═══════════════════════════════════════════════════════════

    // Primary variant
    {
      props: { variant: "primary" as any },
      style: {
        backgroundColor: colors.brand.primary,
        color: colors.text.inverse,

        "&:hover": {
          backgroundColor: colors.brand.primaryHover,
        },

        "&:active": {
          backgroundColor: colors.brand.primaryActive,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.primaryText,
        },
      },
    },

    // Secondary variant
    {
      props: { variant: "secondary" as any },
      style: {
        backgroundColor: colors.brand.secondary,
        color: colors.components.button.secondaryText,

        "&:hover": {
          backgroundColor: colors.brand.primary,
          color: colors.text.inverse,
        },

        "&:active": {
          backgroundColor: colors.brand.secondaryActive,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.secondaryDisabledBg,
          color: colors.components.button.disabledBg,
        },
      },
    },

    // Order-type active variant (ticket header toggle selected state)
    // Figma: Background "Secondary/Main orange", text "Secondary/text Dark"
    {
      props: { variant: "orderTypeActive" as any },
      style: {
        backgroundColor: colors.components.button.secondaryMain,
        color: colors.components.button.secondaryTextDark,

        "&:hover": {
          backgroundColor: colors.components.button.secondaryTabHover,
          color: colors.components.button.secondaryTextDark,
        },

        "&:active": {
          backgroundColor: colors.components.button.secondaryTabHover,
          color: colors.components.button.secondaryTextDark,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.secondaryDisabledBg,
          color: colors.components.button.disabledText,
        },
      },
    },

    // Outlined variant
    {
      props: { variant: "outlined" },
      style: {
        backgroundColor: colors.background.secondary,
        color: colors.brand.primary,
        border: `1px solid ${colors.components.checkbox.background}`,

        "&:hover": {
          backgroundColor: colors.background.hover,
          border: `1px solid ${colors.brand.primary}`,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
          border: `1px solid ${colors.components.button.disabledBg}`,
        },
      },
    },

    // Text variant
    {
      props: { variant: "text" },
      style: {
        backgroundColor: "transparent",
        color: colors.brand.primary,

        "&:hover": {
          backgroundColor: colors.background.hover,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: "transparent",
          color: colors.components.button.disabledText,
        },
      },
    },

    // Light variant
    {
      props: { variant: "light" as any },
      style: {
        backgroundColor: colors.components.button.secondaryBg,
        color: colors.brand.primary,
        border: `1px solid ${colors.components.button.secondaryBg}`,

        "&:hover": {
          backgroundColor: colors.components.button.secondaryHover,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
        },
      },
    },

    // Dimed variant
    {
      props: { variant: "dimed" as any },
      style: {
        backgroundColor: colors.components.button.disabledBg,
        color: colors.text.secondary,

        "&:hover": {
          backgroundColor: colors.background.hover,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════
    // SEMANTIC COLOR VARIANTS
    // ═══════════════════════════════════════════════════════════

    // Success variant
    {
      props: { variant: "success" as any },
      style: {
        backgroundColor: colors.status.successHover,
        color: colors.text.inverse,

        "&:hover": {
          backgroundColor: colors.status.success,
        },

        "&:active": {
          backgroundColor: colors.status.successActive,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
        },
      },
    },

    // Warning variant
    {
      props: { variant: "warning" as any },
      style: {
        backgroundColor: colors.status.warning,
        color: colors.text.inverse,

        "&:hover": {
          backgroundColor: colors.status.warningHover,
        },

        "&:active": {
          backgroundColor: colors.status.warningActive,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
        },
      },
    },

    // Error variant
    {
      props: { variant: "error" as any },
      style: {
        backgroundColor: colors.status.error,
        color: colors.text.inverse,

        "&:hover": {
          backgroundColor: colors.status.errorHover,
        },

        "&:active": {
          backgroundColor: colors.status.errorActive,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
        },
      },
    },

    // Info variant
    {
      props: { variant: "info" as any },
      style: {
        backgroundColor: colors.status.info,
        color: colors.text.inverse,

        "&:hover": {
          backgroundColor: colors.status.infoHover,
        },

        "&:active": {
          backgroundColor: colors.status.infoActive,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
        },
      },
    },

    // Cancel variant
    {
      props: { variant: "cancel" as any },
      style: {
        backgroundColor: "transparent",
        color: colors.text.secondary,
        border: `2px solid ${colors.border.secondary}`,

        "&:hover": {
          backgroundColor: colors.status.errorBg,
          borderColor: colors.status.error,
          color: colors.status.error,
        },

        "&:active": {
          backgroundColor: colors.status.errorBg,
          borderColor: colors.status.error,
        },

        "&.Mui-disabled": {
          backgroundColor: "transparent",
          color: colors.components.button.disabledText,
          borderColor: colors.components.button.disabledBg,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════
    // BACK BUTTON - GENERIC
    // 40 x 40
    // ═══════════════════════════════════════════════════════════

    {
      props: { variant: "back" as any },
      style: {
        width: 48,
        height: 48,
        minWidth: 48,
        minHeight: 48,
        padding: 0,

        border: `1px solid ${colors.components.button.backbuttonBorder}`,
        borderRadius: 8,

        "&:hover": {
          backgroundColor: colors.components.button.backbuttonHover,
          border: `1px solid ${colors.components.button.backbuttonBorder}`,
        },

        "&:active": {
          backgroundColor: colors.components.button.backbuttonClick,
          border: `1px solid ${colors.components.button.backbuttonBorder}`,
        },

        "& .MuiSvgIcon-root": {
          width: 20,
          height: 20,
          display: "block",
        },

        "&.Mui-disabled": {
          backgroundColor: "transparent",
          color: colors.components.button.disabledText,
          borderColor: "rgba(203, 213, 225, 1)",
        },
      },
    },

    // ═══════════════════════════════════════════════════════════
    // BACK BUTTON - POS
    // 48 x 48
    // ═══════════════════════════════════════════════════════════

    {
      props: { variant: "backPos" as any },
      style: {
        width: 48,
        height: 48,
        minWidth: 48,
        minHeight: 48,
        padding: 0,

        border: `1px solid ${colors.components.button.backbuttonBorder}`,
        borderRadius: 8,

        "&:hover": {
          backgroundColor: colors.components.button.backbuttonHover,
          border: `1px solid ${colors.components.button.backbuttonBorder}`,
        },

        "&:active": {
          backgroundColor: colors.components.button.backbuttonClick,
          border: `1px solid ${colors.components.button.backbuttonBorder}`,
        },

        "& .MuiSvgIcon-root": {
          width: 20,
          height: 20,
          display: "block",
        },

        "&.Mui-disabled": {
          backgroundColor: "transparent",
          color: colors.components.button.disabledText,
          borderColor: "rgba(203, 213, 225, 1)",
        },
      },
    },

    // ═══════════════════════════════════════════════════════════
    // BACK BUTTON - BACK OFFICE
    // 40 x 40
    // ═══════════════════════════════════════════════════════════

    {
      props: { variant: "backBackOffice" as any },
      style: {
        width: 40,
        height: 40,
        minWidth: 40,
        minHeight: 40,
        padding: 0,

        border: `1px solid ${colors.components.button.backbuttonBorder}`,
        borderRadius: 8,

        "&:hover": {
          backgroundColor: colors.components.button.backbuttonHover,
          border: `1px solid ${colors.components.button.backbuttonBorder}`,
        },

        "&:active": {
          backgroundColor: colors.components.button.backbuttonClick,
          border: `1px solid ${colors.components.button.backbuttonBorder}`,
        },

        "& .MuiSvgIcon-root": {
          width: 20,
          height: 20,
          display: "block",
        },

        "&.Mui-disabled": {
          backgroundColor: "transparent",
          color: colors.components.button.disabledText,
          borderColor: "rgba(203, 213, 225, 1)",
        },
      },
    },

    // ═══════════════════════════════════════════════════════════
    // SIZE STYLES
    // ═══════════════════════════════════════════════════════════

    {
      props: { size: "small" },
      style: {
        fontSize: "12px",
        padding: "6px 16px",
        minHeight: "32px",
        gap: 6,
      },
    },

    {
      props: { size: "medium" },
      style: {
        fontSize: "16px",
        padding: "8px 20px",
        minHeight: "40px",
        fontWeight: 500,
        gap: 8,
      },
    },

    {
      props: { size: "large" },
      style: {
        fontSize: "16px",
        padding: "12px 24px",
        minHeight: "48px",
        fontWeight: 500,
        gap: 10,
      },
    },

    // ═══════════════════════════════════════════════════════════
    // COMPOSITE VARIANTS
    // ═══════════════════════════════════════════════════════════

    // Toolbar action — icon-led primary CTA pinned in a page/section header, fixed 40px tall
    {
      props: { variant: "toolbarAction" as any },
      style: {
        backgroundColor: colors.brand.primary,
        color: colors.text.inverse,
        height: 40,
        minHeight: 40,
        boxShadow: "none",

        "&:hover": {
          backgroundColor: colors.brand.primaryHover,
          boxShadow: "none",
        },

        "&:active": {
          backgroundColor: colors.brand.primaryActive,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.primaryText,
        },
      },
    },

    // Compact action — primary-colored button with a 14px label (between "small" 12px and "medium" 16px)
    {
      props: { variant: "compactAction" as any },
      style: {
        backgroundColor: colors.brand.primary,
        color: colors.text.inverse,
        fontSize: 14,

        "&:hover": {
          backgroundColor: colors.brand.primaryHover,
        },

        "&:active": {
          backgroundColor: colors.brand.primaryActive,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.primaryText,
        },
      },
    },

    // Attention-outlined — bold, non-wrapping label variant of "outlined" (e.g. an alert/attention trigger chip)
    {
      props: { variant: "attentionOutlined" as any },
      style: {
        backgroundColor: colors.background.secondary,
        color: colors.brand.primary,
        border: `1px solid ${colors.components.checkbox.background}`,
        fontWeight: 600,
        whiteSpace: "nowrap",

        "&:hover": {
          backgroundColor: colors.background.hover,
          border: `1px solid ${colors.brand.primary}`,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
          border: `1px solid ${colors.components.button.disabledBg}`,
        },
      },
    },

    // Cancel (neutral) — muted gray outlined cancel action, no red hover (distinct from "cancel")
    {
      props: { variant: "cancelNeutral" as any },
      style: {
        backgroundColor: colors.background.secondary,
        color: colors.text.secondary,
        border: `1px solid ${colors.border.secondary}`,
        fontWeight: 500,

        "&:hover": {
          backgroundColor: colors.background.hover,
          border: `1px solid ${colors.border.secondary}`,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
          border: `1px solid ${colors.components.button.disabledBg}`,
        },
      },
    },

    // Outlined (secondary-toned) — outlined button using the secondary-button text/border token
    {
      props: { variant: "outlinedSecondary" as any },
      style: {
        backgroundColor: colors.background.secondary,
        color: colors.components.button.secondaryText,
        border: `1px solid ${colors.components.button.secondaryText}`,

        "&:hover": {
          backgroundColor: colors.background.hover,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
          border: `1px solid ${colors.components.button.disabledBg}`,
        },
      },
    },

    // Confirm action — "primary" colors, with a distinct custom disabled treatment
    // (pos/UserHomePage "choose POS" confirm button)
    {
      props: { variant: "confirmAction" as any },
      style: {
        backgroundColor: colors.brand.primary,
        color: colors.text.inverse,

        "&:hover": {
          backgroundColor: colors.brand.primaryHover,
        },

        "&:active": {
          backgroundColor: colors.brand.primaryActive,
        },

        "&.Mui-disabled": {
          backgroundColor: "#8CBBE9",
          color: "white",
          marginTop: "11px",
        },
      },
    },

    // Outlined (muted border) — "outlined" using the generic input-border token instead
    // of the checkbox-border token
    {
      props: { variant: "outlinedMuted" as any },
      style: {
        backgroundColor: colors.background.secondary,
        color: colors.brand.primary,
        border: `1px solid ${colors.components.button.backbuttonBorder}`,

        "&:hover": {
          backgroundColor: colors.background.hover,
          border: `1px solid ${colors.brand.primary}`,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
          border: `1px solid ${colors.components.button.disabledBg}`,
        },
      },
    },

    // Details action — "outlined" tinted with the secondary-brand background (order/table info "details" trigger)
    {
      props: { variant: "detailsAction" as any },
      style: {
        backgroundColor: colors.brand.secondary,
        color: colors.brand.primary,
        border: `1px solid ${colors.components.checkbox.background}`,
        borderRadius: "4px",

        "&:hover": {
          backgroundColor: colors.brand.secondary,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
          border: `1px solid ${colors.components.button.disabledBg}`,
        },
      },
    },

    // Secondary tinted — borderless secondary-brand tint (AmnButtonLarge default look)
    {
      props: { variant: "secondaryTinted" as any },
      style: {
        backgroundColor: colors.brand.secondary,
        color: colors.brand.primary,

        "&:hover": {
          backgroundColor: colors.brand.secondary,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
        },
      },
    },

    // Filter clear — "outlined" using the primary-tier border token instead of the checkbox-border token
    {
      props: { variant: "filterClear" as any },
      style: {
        backgroundColor: colors.background.secondary,
        color: colors.text.secondary,
        border: `1px solid ${colors.border.primary}`,

        "&:hover": {
          backgroundColor: colors.background.hover,
          border: `1px solid ${colors.border.secondary}`,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
          border: `1px solid ${colors.components.button.disabledBg}`,
        },
      },
    },

    // Translate action — light-blue icon-button look (TranslatableTextField's translate trigger)
    {
      props: { variant: "translateAction" as any },
      style: {
        backgroundColor: "#E8F1FB",
        color: "#1976D2",

        "&:hover": {
          backgroundColor: "#E8F1FB",
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
        },
      },
    },

    // Outlined (tertiary-toned) — "outlined" using the tertiary text token for both color and border
    {
      props: { variant: "outlinedTertiary" as any },
      style: {
        backgroundColor: colors.background.secondary,
        color: colors.text.tertiary,
        border: `1px solid ${colors.text.tertiary}`,

        "&:hover": {
          backgroundColor: colors.background.hover,
          border: `1px solid ${colors.brand.primary}`,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
          border: `1px solid ${colors.components.button.disabledBg}`,
        },
      },
    },

    // Danger flat — error-red background with no hover darkening (destructive confirm dialogs)
    {
      props: { variant: "dangerFlat" as any },
      style: {
        backgroundColor: colors.status.error,
        color: colors.background.secondary,

        "&:hover": {
          backgroundColor: colors.status.error,
        },

        "&:active": {
          backgroundColor: colors.status.errorActive,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
        },
      },
    },

    // Primary flat — brand-primary background with an opacity-fade hover instead of a color swap
    {
      props: { variant: "primaryFlat" as any },
      style: {
        backgroundColor: colors.brand.primary,
        color: colors.background.secondary,

        "&:hover": {
          backgroundColor: colors.brand.primary,
          opacity: 0.9,
        },

        "&:active": {
          backgroundColor: colors.brand.primaryActive,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
        },
      },
    },

    // Outlined (primary border) — "outlined" using the brand-primary color for the border too
    {
      props: { variant: "outlinedPrimary" as any },
      style: {
        backgroundColor: colors.background.secondary,
        color: colors.brand.primary,
        border: `1px solid ${colors.brand.primary}`,

        "&:hover": {
          backgroundColor: colors.background.hover,
          border: `1px solid ${colors.brand.primary}`,
        },

        "&:active": {
          backgroundColor: colors.background.selected,
        },

        "&.Mui-disabled": {
          backgroundColor: colors.components.button.disabledBg,
          color: colors.components.button.disabledText,
          border: `1px solid ${colors.components.button.disabledBg}`,
        },
      },
    },
  ],
});
